import { useState, memo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Check as CheckIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import type { PendingScoreUpdate } from '../../types';
import { liveScoresApi } from '../../api';
import { ConfirmDialog } from '../shared/ConfirmDialog';

export interface PendingScoreCardProps {
  pending: PendingScoreUpdate;
  boardId: string;
  title: string;
  homeTeam: string;
  awayTeam: string;
  isManager: boolean;
  onComplete: () => void;
}

export const PendingScoreCard = memo<PendingScoreCardProps>(({
  pending,
  boardId,
  title,
  homeTeam,
  awayTeam,
  isManager,
  onComplete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editHomeScore, setEditHomeScore] = useState(pending.homeScore);
  const [editAwayScore, setEditAwayScore] = useState(pending.awayScore);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissConfirmOpen, setDismissConfirmOpen] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    setError(null);
    try {
      // If scores were edited, save edits first
      if (isEditing) {
        await liveScoresApi.editPendingScore(boardId, pending.id, {
          homeScore: editHomeScore,
          awayScore: editAwayScore,
        });
      }
      await liveScoresApi.approvePendingScore(boardId, pending.id);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve score');
    } finally {
      setLoading(false);
      setIsEditing(false);
    }
  };

  const handleDismiss = async () => {
    setLoading(true);
    setError(null);
    try {
      await liveScoresApi.dismissPendingScore(boardId, pending.id);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to dismiss score');
    } finally {
      setLoading(false);
      setDismissConfirmOpen(false);
    }
  };

  const statusChip = pending.status === 'edited' ? (
    <Chip label="Edited" size="small" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />
  ) : (
    <Chip label="Live" size="small" color="info" sx={{ height: 20, fontSize: '0.65rem' }} />
  );

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          border: '2px solid',
          borderColor: 'info.main',
          bgcolor: 'info.50',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">{title}</Typography>
              {statusChip}
            </Box>
          }
          sx={{ pb: 0, pt: 1.5, px: 2 }}
        />
        <CardContent sx={{ pt: 1, pb: 1, px: 2 }}>
          {error && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
              {error}
            </Typography>
          )}

          {isEditing ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{homeTeam}</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={editHomeScore}
                  onChange={(e) => setEditHomeScore(parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  fullWidth
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>—</Typography>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{awayTeam}</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={editAwayScore}
                  onChange={(e) => setEditAwayScore(parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  fullWidth
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 0.5 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">{homeTeam}</Typography>
                <Typography variant="h6" fontWeight="bold">{pending.homeScore}</Typography>
              </Box>
              <Typography variant="body1" color="text.secondary">—</Typography>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">{awayTeam}</Typography>
                <Typography variant="h6" fontWeight="bold">{pending.awayScore}</Typography>
              </Box>
            </Box>
          )}
        </CardContent>

        {isManager && (
          <CardActions sx={{ justifyContent: 'center', pt: 0, pb: 1.5, gap: 0.5 }}>
            {loading ? (
              <CircularProgress size={24} />
            ) : isEditing ? (
              <>
                <Tooltip title="Save & Approve">
                  <IconButton color="success" onClick={handleApprove} size="small">
                    <SaveIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel edit">
                  <IconButton onClick={() => {
                    setIsEditing(false);
                    setEditHomeScore(pending.homeScore);
                    setEditAwayScore(pending.awayScore);
                  }} size="small">
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="Approve score">
                  <IconButton color="success" onClick={handleApprove} size="small">
                    <CheckIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit score">
                  <IconButton color="primary" onClick={() => setIsEditing(true)} size="small">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Dismiss">
                  <IconButton color="error" onClick={() => setDismissConfirmOpen(true)} size="small">
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </CardActions>
        )}
      </Card>

      <ConfirmDialog
        open={dismissConfirmOpen}
        title="Dismiss Score"
        icon="warning"
        message="Are you sure you want to dismiss this live score update? You can still record it manually later."
        confirmLabel="Dismiss"
        confirmColor="error"
        onConfirm={handleDismiss}
        onCancel={() => setDismissConfirmOpen(false)}
      />
    </>
  );
});

PendingScoreCard.displayName = 'PendingScoreCard';
